import { Field } from '@atlaskit/form';
import AddIcon from '@atlaskit/icon/core/add';
import LinkIcon from '@atlaskit/icon/core/link';
import { Box, Stack, Text, xcss } from '@atlaskit/primitives';
import Select, { type FormatOptionLabelMeta } from '@databases/atlaskit/Select';
import { getCompactSelectStyles } from '@databases/atlaskit/compact-select-styles';
import ParentPicker from '@databases/content-picker/ParentPicker';
import PageTemplatePicker from '@databases/field-picker/PageTemplatePicker';
import { messages as intlMessages } from '@databases/intl/messages';
import { defaultStoreUtils } from '@databases/redux';
import { addChangeToDraft } from '@databases/redux/changesets/actions';
import {
  type ContentReference,
  type TemplateReference
} from '@databases/types/confluence';
import {
  type DatabaseField,
  type DatabaseFieldType,
  PageSubType,
  ParentPageOption
} from '@databases/types/database';
import { DatabaseChangeType } from '@databases/types/redux';
import React, {
  type FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import { useIntl } from 'react-intl-next';

import { messages } from './messages';
import CompactRadioPicker, {
  type CompactRadioPickerOption
} from './shared/CompactRadioPicker';

const LinkType = {
  CREATE_NEW: 'create-new',
  EXISTING: 'existing'
} as const;

type LinkType = (typeof LinkType)[keyof typeof LinkType];

type SelectOption<Value extends string> = {
  label: string;
  value: Value;
};

type ContentLocationOption = SelectOption<ParentPageOption> & {
  description: string;
};

type PageLinkFieldSettingsProps = {
  contentId: string;
  field: DatabaseField<DatabaseFieldType.PAGE_LINK>;
};

const inputWrapperStyles = xcss({
  maxWidth: '100%',
  width: '266px'
});

const dividerStyles = xcss({
  borderBlockStartColor: 'color.border',
  borderBlockStartStyle: 'solid',
  borderBlockStartWidth: 'border.width',
  marginInline: 'space.negative.200',
  marginBlockStart: 'space.200'
});

const fieldGapStyles = xcss({
  borderBlockStartColor: 'transparent',
  borderBlockStartStyle: 'solid',
  borderBlockStartWidth: 'border.width'
});

const contentTypeSelectStyles = getCompactSelectStyles<
  SelectOption<PageSubType>,
  false
>();
const contentLocationSelectStyles = getCompactSelectStyles<
  ContentLocationOption,
  false
>();

const PageLinkFieldSettings: FunctionComponent<PageLinkFieldSettingsProps> = ({
  contentId,
  field
}) => {
  const { formatMessage } = useIntl();
  const dispatch = defaultStoreUtils.useDispatch();
  const fieldLinkType = field.settings.creatable
    ? LinkType.CREATE_NEW
    : LinkType.EXISTING;
  const fieldPageSubType = field.settings.pageSubType;
  const fieldParentPageOption = field.settings.parentPageOption;
  const [linkType, setLinkType] = useState<LinkType>(fieldLinkType);
  const [pageSubType, setPageSubType] = useState(fieldPageSubType);
  const [parentPageOption, setParentPageOption] = useState(fieldParentPageOption);

  useEffect(() => {
    setLinkType(fieldLinkType);
  }, [fieldLinkType]);

  useEffect(() => {
    setPageSubType(fieldPageSubType);
  }, [fieldPageSubType]);

  useEffect(() => {
    setParentPageOption(fieldParentPageOption);
  }, [fieldParentPageOption]);

  const linkTypeLabel = formatMessage(messages.pageLinkTypeLabel);
  const contentTypeLabel = formatMessage(messages.pageLinkContentTypeLabel);
  const contentLocationLabel = formatMessage(messages.pageLinkContentLocationLabel);
  const parentContentLabel = formatMessage(messages.pageLinkParentContentLabel);
  const templateLabel = formatMessage(messages.pageLinkTemplateLabel);

  const linkTypeOptions = useMemo<CompactRadioPickerOption<LinkType>[]>(
    () => [
      {
        icon: <LinkIcon label="" />,
        label: formatMessage(messages.pageLinkTypeExisting),
        value: LinkType.EXISTING
      },
      {
        icon: <AddIcon label="" />,
        label: formatMessage(messages.pageLinkTypeCreateNew),
        value: LinkType.CREATE_NEW
      }
    ],
    [formatMessage]
  );
  const contentTypeOptions = useMemo<SelectOption<PageSubType>[]>(
    () => [
      {
        label: formatMessage(
          intlMessages['field-settings.type-page-link-sub-type-live-doc']
        ),
        value: PageSubType.LIVE
      },
      {
        label: formatMessage(intlMessages['field-settings.type-page-link-sub-type-page']),
        value: PageSubType.PAGE
      }
    ],
    [formatMessage]
  );
  const contentLocationOptions = useMemo<ContentLocationOption[]>(
    () => [
      {
        description: formatMessage(messages.pageLinkContentLocationFixedDescription),
        label: formatMessage(
          intlMessages['field-settings.type-page-link-location-fixed']
        ),
        value: ParentPageOption.FIXED
      },
      {
        description: formatMessage(messages.pageLinkContentLocationDynamicDescription),
        label: formatMessage(
          intlMessages['field-settings.type-page-link-location-dynamic']
        ),
        value: ParentPageOption.DYNAMIC
      }
    ],
    [formatMessage]
  );
  const formatContentLocationOption = useCallback(
    (
      option: ContentLocationOption,
      meta: FormatOptionLabelMeta<ContentLocationOption>
    ) => {
      if (meta.context === 'value') {
        return option.label;
      }

      return (
        <Stack space="space.025">
          <Text>{option.label}</Text>
          <Text color="color.text.subtle" size="small">
            {option.description}
          </Text>
        </Stack>
      );
    },
    []
  );

  const handleLinkTypeChange = useCallback(
    (nextLinkType: LinkType) => {
      setLinkType(nextLinkType);
      const isCreateNew = nextLinkType === LinkType.CREATE_NEW;
      dispatch(
        addChangeToDraft(contentId, {
          type: DatabaseChangeType.SET_IS_CREATABLE,
          fieldId: field.id,
          isCreatable: isCreateNew
        })
      );

      if (isCreateNew && linkType !== LinkType.CREATE_NEW) {
        setPageSubType(PageSubType.LIVE);
        setParentPageOption(ParentPageOption.DYNAMIC);
        dispatch(
          addChangeToDraft(contentId, {
            type: DatabaseChangeType.SET_PAGE_SUB_TYPE,
            fieldId: field.id,
            pageSubType: PageSubType.LIVE
          })
        );
        dispatch(
          addChangeToDraft(contentId, {
            type: DatabaseChangeType.SET_PARENT_PAGE_OPTION,
            fieldId: field.id,
            parentPageOption: ParentPageOption.DYNAMIC
          })
        );
      }
    },
    [contentId, dispatch, field.id, linkType]
  );

  const handleContentTypeChange = useCallback(
    (nextPageSubType: PageSubType) => {
      setPageSubType(nextPageSubType);
      dispatch(
        addChangeToDraft(contentId, {
          type: DatabaseChangeType.SET_PAGE_SUB_TYPE,
          fieldId: field.id,
          pageSubType: nextPageSubType
        })
      );
    },
    [contentId, dispatch, field.id]
  );

  const handleContentLocationChange = useCallback(
    (nextParentPageOption: ParentPageOption) => {
      setParentPageOption(nextParentPageOption);
      dispatch(
        addChangeToDraft(contentId, {
          type: DatabaseChangeType.SET_PARENT_PAGE_OPTION,
          fieldId: field.id,
          parentPageOption: nextParentPageOption
        })
      );
    },
    [contentId, dispatch, field.id]
  );

  const handleSetParentPage = useCallback(
    (newParentPage: ContentReference | undefined) => {
      dispatch(
        addChangeToDraft(contentId, {
          type: DatabaseChangeType.SET_PARENT_PAGE,
          fieldId: field.id,
          newParentPage
        })
      );
    },
    [contentId, dispatch, field.id]
  );

  const handleSetIsParentPageSourceDatabase = useCallback(
    (isParentPageSourceDatabase: boolean) => {
      dispatch(
        addChangeToDraft(contentId, {
          type: DatabaseChangeType.SET_IS_PARENT_PAGE_SOURCE_DATABASE,
          fieldId: field.id,
          isParentPageSourceDatabase
        })
      );
    },
    [contentId, dispatch, field.id]
  );

  const handleSetPageTemplate = useCallback(
    (newPageTemplate: TemplateReference) => {
      dispatch(
        addChangeToDraft(contentId, {
          type: DatabaseChangeType.SET_PAGE_TEMPLATE,
          fieldId: field.id,
          newPageTemplate
        })
      );
    },
    [contentId, dispatch, field.id]
  );

  const selectedContentTypeOption = contentTypeOptions.find(
    (option) => option.value === pageSubType
  );
  const selectedContentLocationOption = contentLocationOptions.find(
    (option) => option.value === parentPageOption
  );

  return (
    <Stack space="space.0">
      <Field
        label={
          <Text size="small" weight="bold" color="color.text.subtle">
            {linkTypeLabel}
          </Text>
        }
        name="page-link-type-field"
      >
        {() => (
          <CompactRadioPicker
            ariaLabel={linkTypeLabel}
            onChange={handleLinkTypeChange}
            options={linkTypeOptions}
            value={linkType}
          />
        )}
      </Field>
      {linkType === LinkType.CREATE_NEW && (
        <>
          <Box xcss={fieldGapStyles} />
          <Stack space="space.0">
            <Field
              label={
                <Text size="small" weight="bold" color="color.text.subtle">
                  {contentTypeLabel}
                </Text>
              }
              name="page-link-content-type-field"
              isRequired
            >
              {({ fieldProps }) => (
                <Box xcss={inputWrapperStyles}>
                  <Select<SelectOption<PageSubType>>
                    classNamePrefix="page-link-content-type"
                    inputId={fieldProps.id}
                    isClearable={false}
                    isSearchable={false}
                    onChange={(option) => handleContentTypeChange(option!.value)}
                    options={contentTypeOptions}
                    spacing="compact"
                    styles={contentTypeSelectStyles}
                    value={selectedContentTypeOption}
                  />
                </Box>
              )}
            </Field>
            <Box xcss={fieldGapStyles} />
            <Field
              label={
                <Text size="small" weight="bold" color="color.text.subtle">
                  {contentLocationLabel}
                </Text>
              }
              name="page-link-content-location-field"
              isRequired
            >
              {({ fieldProps }) => (
                <Box xcss={inputWrapperStyles}>
                  <Select<ContentLocationOption>
                    classNamePrefix="page-link-content-location"
                    formatOptionLabel={formatContentLocationOption}
                    inputId={fieldProps.id}
                    isClearable={false}
                    isSearchable={false}
                    onChange={(option) => handleContentLocationChange(option!.value)}
                    options={contentLocationOptions}
                    spacing="compact"
                    styles={contentLocationSelectStyles}
                    value={selectedContentLocationOption}
                  />
                </Box>
              )}
            </Field>
          </Stack>
          {parentPageOption === ParentPageOption.FIXED && (
            <>
              <Box xcss={fieldGapStyles} />
              <Field
                label={
                  <Text size="small" weight="bold" color="color.text.subtle">
                    {parentContentLabel}
                  </Text>
                }
                id="page-link-parent-content-picker-input"
                name="page-link-parent-content-field"
                isRequired
              >
                {({ fieldProps }) => (
                  <ParentPicker
                    contentId={contentId}
                    fieldId={field.id}
                    handleSetIsParentPageSourceDatabase={
                      handleSetIsParentPageSourceDatabase
                    }
                    handleSetParentPage={handleSetParentPage}
                    isSourceOption={
                      Boolean(field.settings.isParentPageSourceDatabase) &&
                      !field.settings.parentPage
                    }
                    inputId={fieldProps.id}
                    isCompact
                    value={field.settings.parentPage}
                  />
                )}
              </Field>
              <Box xcss={fieldGapStyles} />
              <Field
                label={
                  <Text size="small" weight="bold" color="color.text.subtle">
                    {templateLabel}
                  </Text>
                }
                id="page-link-template-picker-input"
                name="page-link-template-field"
              >
                {({ fieldProps }) => (
                  <PageTemplatePicker
                    inputId={fieldProps.id}
                    isCompact
                    onChange={handleSetPageTemplate}
                    value={field.settings.pageTemplate}
                  />
                )}
              </Field>
            </>
          )}
        </>
      )}
      <Box role="separator" xcss={dividerStyles} />
    </Stack>
  );
};

export default PageLinkFieldSettings;
